<template>
  <v-container fluid class="pa-4 bg-grey-lighten-4" style="min-height: 100vh;">
    <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-4" />

    <!-- Heading Section -->
    <v-row class="align-center mb-4">
      <v-col cols="12" md="4">
        <h2 class="text-h5 font-weight-bold">Covered Call for NIFTY50</h2>
      </v-col>
      <v-col cols="12" md="8" class="d-flex justify-end align-center">
        <v-btn icon variant="text" size="small" class="mr-2" @click="fetchSetupData">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
        <v-btn variant="text" color="primary" class="text-none" @click="showMarginDialog = true">MARGIN</v-btn>
        <v-spacer></v-spacer>
        <NiftyLevel v-slot="{ currentLevel }">
          <span class="text-caption font-weight-bold">
            NIFTY: <span :class="currentLevel >= setupData.niftyStartLevel ? 'text-green' : 'text-red'">
              {{ currentLevel }} 
              ({{ (currentLevel - (setupData.niftyStartLevel || 0)).toFixed(2) }})
            </span>
          </span>
        </NiftyLevel>
      </v-col>
    </v-row>

    <!-- 1. Selection / Input Card (Populated by API) -->
    <v-card class="mb-4 elevation-1">
      <v-card-title class="text-subtitle-1 font-weight-bold">Selection</v-card-title>
      <v-card-text>
        <v-row class="align-center mb-6">
          <v-col cols="12" md="2">
            <span class="font-weight-medium">NIFTY Level:</span>
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field 
              v-model="setupData.niftyStartLevel" 
              density="compact" 
              variant="underlined" 
              hide-details 
              readonly
            ></v-text-field>
          </v-col>
        </v-row>

        <!-- Dynamic Rows based on API NiftyOptionData -->
       <!-- Dynamic Rows based on API NiftyOptionData -->
        <v-row v-for="(option, key) in mappedOptions" :key="key" class="mb-2">
          <template v-if="option.data">
            <v-col cols="12" md="4">
              <v-text-field 
                :label="option.label" 
                v-model="option.data.strike" 
                density="compact" 
                variant="outlined" 
                hide-details
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field 
                label="Expiry" 
                v-model="option.data.expiryDate" 
                density="compact" 
                variant="outlined" 
                hide-details
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="4">
              <v-text-field 
                label="Price" 
                v-model="option.data.price" 
                density="compact" 
                variant="outlined" 
                hide-details
              ></v-text-field>
            </v-col>
          </template>
        </v-row>

        <v-btn color="primary" class="mt-4 text-none" @click="showTradeTable = true">
          ShowTrade
        </v-btn>
      </v-card-text>
    </v-card>

    <!-- 2. Trading Symbols Table (The Data Table) -->
    <v-card v-if="showTradeTable" class="elevation-1">
      <v-data-table
        :headers="tableHeaders"
        :items="tradeItems"
        hide-default-footer
        class="custom-trade-table"
      >
        <template #[`item.app`]>
          <v-img src="/zerodha.png" max-width="25" />
        </template>
        
        <template #[`item.actionControls`]>
          <v-icon color="primary" class="cursor-pointer">mdi-pencil</v-icon>
        </template>
      </v-data-table>

      <v-card-actions class="justify-center pa-6">
        <v-btn 
          color="green-darken-1" 
          variant="elevated" 
          size="large" 
          class="px-10 text-none font-weight-bold"
          @click="handleExecuteTrade"
        >
          Execute Trade
        </v-btn>
      </v-card-actions>
    </v-card>

    <!-- Margin Dialog from your sample -->
    <v-dialog v-model="showMarginDialog" max-width="800">
      <v-card>
        <v-card-title>Margin Details</v-card-title>
        <v-card-text>
          <v-data-table :headers="marginHeaders" :items="marginData" class="elevation-1" hide-default-footer />
        </v-card-text>
        <v-card-actions><v-spacer /><v-btn text @click="showMarginDialog = false">Close</v-btn></v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue'
import { useStore } from 'vuex'
import NiftyLevel from '@/components/trades/NiftyLevel.vue'

const store = useStore()
const loading = ref(false)
const showTradeTable = ref(false)
const showMarginDialog = ref(false)

// State for API response
const setupData = ref({
  niftyStartLevel: null,
  weeklyNiftyOptionData: {},
  monthlyNiftyOptionData: {},
  coveredPutNiftyOptionData: {},
  niftyLevelUpperThreshold: null
})

// Mapped options for cleaner iteration in template
const mappedOptions = computed(() => [
  { label: 'Weekly CE Level', data: setupData.value.weeklyNiftyOptionData },
  { label: 'Monthly CE Level', data: setupData.value.monthlyNiftyOptionData },
  { label: 'Covered PE Level', data: setupData.value.coveredPutNiftyOptionData }
])

// Headers for the "ShowTrade" table
const tableHeaders = [
  { title: 'App', key: 'app', sortable: false },
  { title: 'Symbol', key: 'tradingSymbol' },
  { title: 'Trade Type', key: 'tradeType' },
  { title: 'Type', key: 'type' },
  { title: 'Qty', key: 'qty' },
  { title: 'Expiry', key: 'expiryDate' },
  { title: 'Price', key: 'ltp' }
]

// Transform API data into table rows
const tradeItems = computed(() => {
  // Guard clause to prevent errors if API data hasn't loaded
  if (!setupData.value || !setupData.value.weeklyNiftyOptionData?.tradingSymbol) {
    return [];
  }

  // Use the lot size directly from the API response
  const lotSize = setupData.value.niftyLotSize || 0;

  return [
    { 
      ...setupData.value.weeklyNiftyOptionData, 
      symbol: setupData.value.weeklyNiftyOptionData.tradingSymbol, 
      tradeType: 'CE-w', 
      type: 'SELL', 
      qty: lotSize,
      buyPrice: 0, 
      ltp: setupData.value.weeklyNiftyOptionData.price,
      positionStatus: 'READY'
    },
    { 
      ...setupData.value.monthlyNiftyOptionData, 
      symbol: setupData.value.monthlyNiftyOptionData.tradingSymbol,
      tradeType: 'CE-m', 
      type: 'SELL', 
      qty: lotSize,
      buyPrice: 0,
      ltp: setupData.value.monthlyNiftyOptionData.price,
      positionStatus: 'READY'
    },
    { 
      ...setupData.value.coveredPutNiftyOptionData, 
      symbol: setupData.value.coveredPutNiftyOptionData.tradingSymbol,
      tradeType: 'PE', 
      type: 'BUY', 
      qty: lotSize,
      buyPrice: 0,
      ltp: setupData.value.coveredPutNiftyOptionData.price,
      positionStatus: 'READY'
    }
  ];
});

const fetchSetupData = async () => {
  try {
    console.log('Fetching covered call setup...');
    loading.value = true
    const data = await store.dispatch('coveredcall/getCoveredCallSetup', {})
    
    // Check if data exists and extract the 'initSetup' object
    if (data && data.initSetup) {
      setupData.value = data.initSetup
      console.log('Setup data:', setupData.value);
    } else if (data) {
      // Fallback just in case your Vuex store already unwraps it
      setupData.value = data 
    }
  } catch (err) {
    store.commit('snackbar/showMessage', { text: 'Failed to load setup', color: 'error' })
  } finally {
    loading.value = false
  }
}

const handleExecuteTrade = () => {
  // Add execution logic here
  console.log('Executing Trades for symbols:', tradeItems.value.map(i => i.tradingSymbol))
}

onMounted(() => {
  fetchSetupData()
})
</script>

<style scoped>
.custom-trade-table :deep(th) {
  background-color: #fbeee6 !important;
  font-weight: bold !important;
}
.text-red { color: #e53935; }
.text-green { color: #43a047; }
.cursor-pointer { cursor: pointer; }
</style>